import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { usePhenologyForProducts } from '/@/hooks/recommendation';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import type { Field } from '/@/types/field';
import { useHybrids } from '../hybrids/use-hybrids';
import { FALLBACK_HYBRID_COLOR, HYBRID_COLORS } from './use-management-data';

/**
 * The critical period runs a fortnight either side of flowering. Mirrors
 * `calculateCornPhenology`, so the wizard and the rest of the app agree.
 */
export const CRITICAL_PERIOD_DAYS = 15;

export type PhenologyStage = { date?: string; gdus?: number };

export type HybridPhenology = {
    id: number;
    catalogName: string;
    color: string;
    planting?: string;
    emergence?: string;
    /** Sixth leaf, inside the vegetative leg. Null when the leaf coefficients don't resolve. */
    v6?: string;
    flowering?: string;
    maturity?: string;
    /** GDUs for each leg of the cycle — planting→emergence, →flowering, →maturity. */
    gdus: { emergence?: number; flowering?: number; maturity?: number };
    totalGdus?: number;
    criticalPeriod?: { start: string; end: string };
};

type PhenologyResponse = Record<
    number,
    | {
          planting?: string;
          emergence?: string;
          v6?: string | null;
          flowering?: string;
          maturity?: string;
          gdus?: { emergence?: number; flowering?: number; maturity?: number };
      }
    | undefined
>;

/**
 * Stage dates for the selected hybrids at a given planting date.
 *
 * `plantingDate` is a parameter rather than read from the crop config so the same hook can
 * drive the simulation, which previews another date without touching the stored config.
 */
export const usePhenology = ({
    field,
    crop = 'corn',
    plantingDate,
}: {
    field?: Field;
    crop?: 'corn' | 'soy';
    plantingDate?: string;
}) => {
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);
    const productIds: number[] = cropConfig?.products || [];

    const { featuredProducts, otherProducts } = useHybrids({ field, crop });
    const catalog = useMemo(() => [...featuredProducts, ...otherProducts], [featuredProducts, otherProducts]);

    const phenologyQuery = usePhenologyForProducts({
        crop,
        lat: field?.lat,
        lng: field?.lng,
        productsId: productIds,
        plantingDate,
    });

    const hybrids: HybridPhenology[] = useMemo(() => {
        const data = phenologyQuery.data as PhenologyResponse | undefined;
        return productIds.map((id, index) => {
            const product = catalog.find((candidate) => candidate.id === id);
            const phenology = data?.[id];
            const gdus = phenology?.gdus || {};
            const segments = [gdus.emergence, gdus.flowering, gdus.maturity].filter(
                (value): value is number => typeof value === 'number'
            );

            return {
                id,
                catalogName: product?.catalogName || '',
                color: HYBRID_COLORS[index % HYBRID_COLORS.length] ?? FALLBACK_HYBRID_COLOR,
                planting: phenology?.planting,
                emergence: phenology?.emergence,
                v6: phenology?.v6 ?? undefined,
                flowering: phenology?.flowering,
                maturity: phenology?.maturity,
                gdus,
                totalGdus: segments.length ? segments.reduce((total, value) => total + value, 0) : undefined,
                criticalPeriod: phenology?.flowering
                    ? {
                          start: dayjs(phenology.flowering).subtract(CRITICAL_PERIOD_DAYS, 'day').toISOString(),
                          end: dayjs(phenology.flowering).add(CRITICAL_PERIOD_DAYS, 'day').toISOString(),
                      }
                    : undefined,
            };
        });
    }, [productIds, catalog, phenologyQuery.data]);

    return {
        hybrids,
        isLoading: phenologyQuery.isLoading || (phenologyQuery.isValidating && !phenologyQuery.data),
    };
};
